import type { GridRawRowDataType } from '@/common/components/molecules/modernGrid/modernGrid.types';

export type CheckEventResult<T> =
  | { type: 'checkAll'; isCheck: boolean; rows: T[] }
  | { type: 'checkRow'; isCheck: boolean; row: T; rowIndex: number }
  | null;

export const isSameRow = <T extends GridRawRowDataType>(a: T, b: T): boolean => {
  const aId = (a as Record<string, unknown>).rowId;
  const bId = (b as Record<string, unknown>).rowId;

  if (aId !== undefined && bId !== undefined) return aId === bId;
  if (a === b) return true;

  const keysA = Object.keys(a as object).slice(0, 5);
  return keysA.every(
    (key) => (a as Record<string, unknown>)[key] === (b as Record<string, unknown>)[key],
  );
};

export const findRowIndex = <T extends GridRawRowDataType>(rows: T[], target: T): number => {
  return rows.findIndex((row) => isSameRow(row, target));
};

export const determineCheckEvent = <T extends GridRawRowDataType>(
  prevRows: T[],
  newRows: T[],
  allRows: T[],
): CheckEventResult<T> => {
  const prevCount = prevRows.length;
  const newCount = newRows.length;
  const totalCount = allRows.length;

  if (prevCount !== totalCount && newCount === totalCount && totalCount > 0) {
    return { type: 'checkAll', isCheck: true, rows: newRows };
  }

  if (prevCount === totalCount && totalCount > 0 && newCount === 0) {
    return { type: 'checkAll', isCheck: false, rows: [] };
  }

  const diff = newCount - prevCount;

  if (diff === 1) {
    const addedRow = newRows.find((row) => findRowIndex(prevRows, row) === -1);
    if (addedRow) {
      return {
        type: 'checkRow',
        isCheck: true,
        row: addedRow,
        rowIndex: findRowIndex(allRows, addedRow),
      };
    }
  } else if (diff === -1) {
    const removedRow = prevRows.find((row) => findRowIndex(newRows, row) === -1);
    if (removedRow) {
      return {
        type: 'checkRow',
        isCheck: false,
        row: removedRow,
        rowIndex: findRowIndex(allRows, removedRow),
      };
    }
  } else if (diff > 1) {
    return { type: 'checkAll', isCheck: true, rows: newRows };
  } else if (diff < -1) {
    return { type: 'checkAll', isCheck: false, rows: newRows };
  }

  return null;
};
